import star from '@/assets/icons/level-icon-md.svg';
import badgeBg from '@/assets/pages/badges-store/item.png';
import tg from '@/assets/pages/missions/telegram-icn-md.svg';
import yt from '@/assets/pages/missions/youtube-icn-md.svg';
import { cdn } from '@/constants/api';
import { mapApiBadgeStoreTypes } from '@/constants/badges';
import { missionTypes, missionVisibility } from '@/constants/missions';
import {
  MissionSettingsResponse,
  PlayerLevelResponse,
  BadgeSettingsResponse,
} from '@/types/api';
import {
  MissionChannel,
  MissionCustom,
  MissionLink,
  PlayerLevel,
} from '@/types/models';
import { Badges } from '@/types/models/badge';

export const mapPlayerLevels = (list: PlayerLevelResponse[]) =>
  list.reduce<Record<string, PlayerLevel>>(
    (levels, level) => ({
      ...levels,
      [level.level]: {
        energy: level.energy,
        femaleAvatar: level.female_avatar,
        id: level.id,
        income: level.income,
        maleAvatar: level.male_avatar,
        level: level.level,
        requiredXp: level.required_xp,
        tapPower: level.tap_power,
        energyRestore: level.energy_restore,
        rewardKeys: level.reward_keys,
      },
    }),
    {},
  );

const mapChannelMission = (
  mission: MissionSettingsResponse,
): MissionChannel => ({
  id: mission.id,
  externalId: mission.external_id,
  name: `app.missions.${mission.external_id}.name`,
  description: `app.missions.${mission.external_id}.description`,
  action: `app.missions.${mission.external_id}.action`,
  icon: mission.icon ? `${cdn}/${mission.icon}` : tg,
  isActive: mission.is_active,
  visibility: mission.visibility,
  xp: mission.reward_xp,
  coins: mission.reward_coins,
  keys: mission.reward_keys,
  channelId: mission.channel_id,
  link: mission.link,
  duration: mission.duration,
  type: mission.type as typeof missionTypes.channel,
});

const mapCustomMission = (mission: MissionSettingsResponse): MissionCustom => ({
  id: mission.id,
  externalId: mission.external_id,
  name: `app.missions.${mission.external_id}.name`,
  description: `app.missions.${mission.external_id}.description`,
  action: `app.missions.${mission.external_id}.action`,
  icon: mission.icon ? `${cdn}/${mission.icon}` : star,
  visibility: mission.visibility,
  isActive: mission.is_active,
  xp: mission.reward_xp,
  coins: mission.reward_coins,
  keys: mission.reward_keys,
  channelId: mission.channel_id,
  link: mission.link,
  duration: mission.duration,
  type: mission.type as typeof missionTypes.custom,
});

const mapLinkMission = (mission: MissionSettingsResponse): MissionLink => ({
  id: mission.id,
  externalId: mission.external_id,
  name: `app.missions.${mission.external_id}.name`,
  description: `app.missions.${mission.external_id}.description`,
  action: `app.missions.${mission.external_id}.action`,
  icon: mission.icon ? `${cdn}/${mission.icon}` : yt,
  isActive: mission.is_active,
  visibility: mission.visibility,
  xp: mission.reward_xp,
  coins: mission.reward_coins,
  keys: mission.reward_keys,
  channelId: mission.channel_id,
  link: mission.link,
  duration: mission.duration,
  type: mission.type as typeof missionTypes.link,
});

export const mapMissions = (list: MissionSettingsResponse[]) =>
  list.reduce((missions, mission) => {
    if (mission.visibility !== missionVisibility.wallet) {
      return missions;
    }
    if (mission.type === missionTypes.channel) {
      return {
        ...missions,
        [mission.id]: mapChannelMission(mission),
      };
    }
    if (mission.type === missionTypes.custom) {
      return {
        ...missions,
        [mission.id]: mapCustomMission(mission),
      };
    }
    if (mission.type === missionTypes.link) {
      return {
        ...missions,
        [mission.id]: mapLinkMission(mission),
      };
    }
    return missions;
  }, {});

export const mapStoreBadges = (list: BadgeSettingsResponse[] = []): Badges =>
  list.reduce<Badges>(
    (badges, badge) => ({
      ...badges,
      [badge.external_id]: {
        externalId: badge.external_id,
        id: badge.id,
        image: badge.image ? `${cdn}/${badge.image}` : badgeBg,
        name: `app.badges.${badge.external_id}.name`,
        type: mapApiBadgeStoreTypes[badge.rarity],
        isActive: badge.is_active,
        keysPrice: badge.keys_price,
      },
    }),
    {},
  );
